<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-6 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <button @click="$router.back()" class="text-white mr-4">
              <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <div>
              <h1 class="text-xl font-bold text-white">Personal Schedule</h1>
              <p class="text-blue-100 text-sm">Your custom events and tasks</p>
            </div>
          </div>
          <button 
            v-if="!showAddModal && !editingEntry"
            @click="showAddModal = true"
            class="bg-white text-blue-900 px-4 py-2 rounded-lg flex items-center hover:bg-blue-50"
          >
            <span class="material-symbols-outlined mr-1">add</span>
            Add Entry
          </button>
          <button 
            v-else
            @click="cancelEdit"
            class="px-4 py-2 border border-white text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center"
          >
            <span class="material-symbols-outlined mr-1">close</span>
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
    </div>

    <!-- Content -->
    <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Next Event Smart Card -->
      <div v-if="nextEventInfo" class="bg-blue-50 border-l-4 rounded-lg p-4 mb-4 flex items-center gap-4 shadow-sm" :style="{ borderColor: nextEventInfo.entry.color }">
        <span class="material-symbols-outlined text-3xl" :style="{ color: nextEventInfo.entry.color }">schedule</span>
        <div>
          <div class="font-semibold text-blue-900 text-base mb-1">
            Next: {{ nextEventInfo.entry.title }}
          </div>
          <div class="text-sm text-blue-800">
            {{ formatDate(nextEventInfo.entry.date) }}, {{ nextEventInfo.entry.startTime }} - {{ nextEventInfo.entry.endTime }}
            <span v-if="nextEventInfo.entry.location"> at {{ nextEventInfo.entry.location }}</span>
          </div>
          <div class="mt-1">
            <span
              :class=" [
                'inline-block px-3 py-1 rounded-full text-xs font-semibold',
                nextEventInfo.ms <= 5 * 60 * 1000
                  ? 'bg-red-500 text-white animate-pulse'
                  : nextEventInfo.ms <= 15 * 60 * 1000
                    ? 'bg-yellow-400 text-gray-900'
                    : 'bg-green-500 text-white'
              ]"
              v-if="typeof nextEventInfo.ms === 'number'"
            >
              {{ nextEventInfo.status }}
            </span>
            <span v-else class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-500">
              {{ nextEventInfo.status }}
            </span>
          </div>
        </div>
      </div>

      <!-- Add/Edit Form (conditionally shown) -->
      <div v-if="showAddModal || editingEntry" class="bg-white rounded-lg shadow p-6 mb-6">
        <h3 class="font-medium text-gray-800 mb-4 flex items-center">
          <span class="material-symbols-outlined mr-2">{{ editingEntry ? 'edit' : 'add' }}</span>
          {{ editingEntry ? 'Update Entry' : 'Add New Entry' }}
        </h3>
        
        <form @submit.prevent="saveEntry">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input 
                v-model="formData.title"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
                placeholder="e.g., Study Group Meeting"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select v-model="formData.type" required class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900">
                <option value="event">Event</option>
                <option value="task">Task</option>
                <option value="reminder">Reminder</option>
                <option value="study-session">Study Session</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input 
                v-model="formData.date"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input 
                v-model="formData.location"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
                placeholder="Optional"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
              <input 
                v-model="formData.startTime"
                type="time"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
              <input 
                v-model="formData.endTime"
                type="time"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
              />
              <p v-if="timeError" class="mt-1 text-xs text-red-600">{{ timeError }}</p>
            </div>
          </div>

          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea 
              v-model="formData.description"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-900 focus:border-blue-900"
              rows="3"
              placeholder="Optional"
            ></textarea>
          </div>

          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div class="flex gap-2">
              <button 
                v-for="color in colors" 
                :key="color"
                type="button"
                @click="formData.color = color"
                :class=" [
                  'w-10 h-10 rounded-full border-2 transition-all',
                  formData.color === color ? 'border-blue-900 scale-110' : 'border-gray-300 hover:scale-105'
                ]"
                :style="{ backgroundColor: color }"
              ></button>
            </div>
          </div>

          <div class="flex gap-2 pt-4 mt-4 border-t border-gray-200">
            <button 
              type="submit"
              :disabled="isSubmitting || !!timeError"
              class="flex-1 bg-blue-900 text-white py-2 rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting">
                <span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Processing...
              </span>
              <span v-else>
                {{ editingEntry ? 'Update' : 'Add' }}
              </span>
            </button>
            <button 
              type="button"
              @click="cancelEdit"
              class="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <!-- Filter Tabs -->
      <div class="bg-white rounded-lg shadow mb-6 p-4">
        <div class="flex gap-2 flex-wrap overflow-x-auto">
          <button 
            v-for="filter in filters" 
            :key="filter.value"
            @click="currentFilter = filter.value"
            :class=" [
              'px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-colors',
              currentFilter === filter.value 
                ? 'bg-blue-900 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
            style="min-width: 110px;"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <!-- Entries List -->
      <div v-if="filteredEntries.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
        <span class="material-symbols-outlined text-6xl text-gray-300 mb-4">event_note</span>
        <p class="text-gray-500">No entries yet. Start by adding your first event or task!</p>
      </div>

      <div v-else class="space-y-4">
        <div 
          v-for="entry in filteredEntries" 
          :key="entry.id"
          class="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow border-l-4"
          :style="{ borderLeftColor: entry.color }"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start flex-1">
              <!-- Color indicator dot -->
              <div 
                class="w-3 h-3 rounded-full mt-1 mr-3 flex-shrink-0" 
                :style="{ backgroundColor: entry.color }"
              ></div>
              
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <h3 class="font-semibold text-lg" :class="entry.isCompleted ? 'line-through text-gray-500' : ''">
                    {{ entry.title }}
                  </h3>
                  <span 
                    class="text-xs px-2 py-1 rounded-full"
                    :class="getTypeStyles(entry.type)"
                  >
                    {{ entry.type }}
                  </span>
                </div>
                
                <div class="text-sm text-gray-600 space-y-1">
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-sm mr-1">calendar_today</span>
                    {{ formatDate(entry.date) }}
                  </div>
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-sm mr-1">schedule</span>
                    {{ entry.startTime }} - {{ entry.endTime }}
                  </div>
                  <div v-if="entry.location" class="flex items-center">
                    <span class="material-symbols-outlined text-sm mr-1">location_on</span>
                    {{ entry.location }}
                  </div>
                  <p v-if="entry.description" class="text-gray-500 mt-2 text-left">
                    {{ entry.description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2 ml-4">
              <button 
                v-if="entry.type === 'task'"
                @click="toggleCompletion(entry)"
                class="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <span class="material-symbols-outlined" :class="entry.isCompleted ? 'text-green-600' : 'text-gray-400'">
                  {{ entry.isCompleted ? 'check_circle' : 'radio_button_unchecked' }}
                </span>
              </button>
              <button 
                @click="editEntry(entry)"
                class="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <span class="material-symbols-outlined text-blue-600">edit</span>
              </button>
              <button 
                @click="confirmDeleteEntry(entry)"
                class="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <span class="material-symbols-outlined text-red-600">delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="entryToDelete" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-5 max-w-md w-full shadow-xl">
        <div class="flex justify-between items-start mb-4">
          <div class="flex items-center">
            <span class="material-symbols-outlined text-red-600 mr-2">warning</span>
            <h3 class="font-semibold text-lg">Delete Entry</h3>
          </div>
          <button @click="entryToDelete = null" class="text-gray-500 hover:text-gray-700">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <p class="mb-4">
          Are you sure you want to delete this entry? This action cannot be undone.
        </p>
        <p class="font-medium mb-6">
          {{ entryToDelete?.title }}
        </p>
        <div class="flex justify-end space-x-3">
          <button 
            @click="entryToDelete = null"
            class="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            @click="deleteEntry"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import { mapState } from 'vuex';
import { 
  getPersonalTimetable, 
  addPersonalTimetableEntry, 
  updatePersonalTimetableEntry, 
  deletePersonalTimetableEntry,
  togglePersonalTimetableCompletion
} from '@/composables/usePersonalTimetable';

export default {
  components: {
    AppHeader,
    BottomNav
  },
  data() {
    return {
      entries: [],
      isLoading: false,
      showAddModal: false,
      editingEntry: null,
      entryToDelete: null,
      currentFilter: 'all',
      isSubmitting: false,
      timeError: '',
      nextEventNow: new Date(),
      nextEventInterval: null,
      filters: [
        { label: 'All', value: 'all' },
        { label: 'Events', value: 'event' },
        { label: 'Tasks', value: 'task' },
        { label: 'Reminders', value: 'reminder' },
        { label: 'Study Sessions', value: 'study-session' }
      ],
      colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'],
      formData: {
        title: '',
        type: 'event',
        date: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        color: '#3B82F6'
      }
    };
  },
  computed: {
    ...mapState(['user']),
    
    filteredEntries() {
      let filtered = this.entries;
      
      if (this.currentFilter !== 'all') {
        filtered = filtered.filter(entry => entry.type === this.currentFilter);
      }
      
      return filtered.sort((a, b) => new Date(a.date + ' ' + a.startTime) - new Date(b.date + ' ' + b.startTime));
    },

    nextEventInfo() {
      if (!this.entries || this.entries.length === 0) return null;
      
      const now = this.nextEventNow || new Date();
      
      const upcomingEntries = this.entries
        .filter(entry => !entry.isCompleted)
        .map(entry => {
          const eventDateTime = new Date(`${entry.date}T${entry.startTime}`);
          return { ...entry, _eventDateTime: eventDateTime };
        })
        .filter(entry => entry._eventDateTime > now)
        .sort((a, b) => a._eventDateTime - b._eventDateTime);
      
      if (upcomingEntries.length === 0) return null;
      
      const next = upcomingEntries[0];
      const diffMs = next._eventDateTime - now;
      
      // Calculate days, hours, minutes, seconds
      let remaining = diffMs;
      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      remaining -= days * (1000 * 60 * 60 * 24);
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      remaining -= hours * (1000 * 60 * 60);
      const minutes = Math.floor(remaining / (1000 * 60));
      remaining -= minutes * (1000 * 60);
      const seconds = Math.floor(remaining / 1000);
      
      // Build string
      const parts = [];
      if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
      if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
      if (minutes > 0) parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
      parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);
      
      return {
        entry: next,
        ms: diffMs,
        status: diffMs === 0 ? 'Now' : `Starts in ${parts.join(' ')}`
      };
    }
  },
  watch: {
    'formData.startTime'() {
      this.validateTimes();
    },
    'formData.endTime'() {
      this.validateTimes();
    }
  },
  mounted() {
    this.loadEntries();
    // Start interval for real-time countdown
    this.nextEventInterval = setInterval(() => {
      this.nextEventNow = new Date();
    }, 1000);
  },
  beforeUnmount() {
    if (this.nextEventInterval) {
      clearInterval(this.nextEventInterval);
    }
  },
  methods: {
    async loadEntries() {
      if (!this.user || !this.user.uid) return;
      
      this.isLoading = true;
      try {
        const result = await getPersonalTimetable(this.user.uid);
        if (result.success) {
          this.entries = result.entries;
        }
      } catch (error) {
        console.error('Error loading personal timetable:', error);
        this.$store.dispatch('showAlert', { 
          message: 'Failed to load entries', 
          type: 'error' 
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    validateTimes() {
      if (this.formData.startTime && this.formData.endTime) {
        const start = new Date(`2000-01-01T${this.formData.startTime}`);
        const end = new Date(`2000-01-01T${this.formData.endTime}`);
        if (start >= end) {
          this.timeError = 'End time must be after start time';
        } else {
          this.timeError = '';
        }
      } else {
        this.timeError = '';
      }
    },
    
    async saveEntry() {
      this.validateTimes();
      if (this.timeError) return;
      
      if (!this.validateForm()) return;
      
      this.isSubmitting = true;
      try {
        if (this.editingEntry) {
          const result = await updatePersonalTimetableEntry(
            this.user.uid, 
            this.editingEntry.id, 
            this.formData
          );
          if (result.success) {
            this.$store.dispatch('showAlert', { 
              message: 'Entry updated successfully', 
              type: 'success' 
            });
          }
        } else {
          const result = await addPersonalTimetableEntry(this.user.uid, this.formData);
          if (result.success) {
            this.$store.dispatch('showAlert', { 
              message: 'Entry added successfully', 
              type: 'success' 
            });
          }
        }
        
        this.showAddModal = false;
        this.resetForm();
        await this.loadEntries();
      } catch (error) {
        console.error('Error saving entry:', error);
        this.$store.dispatch('showAlert', { 
          message: 'Failed to save entry', 
          type: 'error' 
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    
    editEntry(entry) {
      this.editingEntry = entry;
      this.formData = { ...entry };
      this.showAddModal = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    confirmDeleteEntry(entry) {
      this.entryToDelete = entry;
    },
    
    async deleteEntry() {
      if (!this.entryToDelete?.id) {
        this.entryToDelete = null;
        return;
      }
      
      this.isLoading = true;
      try {
        const result = await deletePersonalTimetableEntry(this.user.uid, this.entryToDelete.id);
        if (result.success) {
          this.$store.dispatch('showAlert', { 
            message: 'Entry deleted successfully', 
            type: 'success' 
          });
          await this.loadEntries();
        }
      } catch (error) {
        console.error('Error deleting entry:', error);
        this.$store.dispatch('showAlert', { 
          message: 'Failed to delete entry', 
          type: 'error' 
        });
      } finally {
        this.isLoading = false;
        this.entryToDelete = null;
      }
    },
    
    async toggleCompletion(entry) {
      try {
        const result = await togglePersonalTimetableCompletion(
          this.user.uid, 
          entry.id, 
          !entry.isCompleted
        );
        if (result.success) {
          await this.loadEntries();
        }
      } catch (error) {
        console.error('Error toggling completion:', error);
      }
    },
    
    validateForm() {
      if (!this.formData.title || !this.formData.date || 
          !this.formData.startTime || !this.formData.endTime) {
        this.$store.dispatch('showAlert', { 
          message: 'Please fill in all required fields', 
          type: 'error' 
        });
        return false;
      }
      return true;
    },
    
    resetForm() {
      this.formData = {
        title: '',
        type: 'event',
        date: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        color: '#3B82F6'
      };
      this.editingEntry = null;
      this.timeError = '';
    },
    
    cancelEdit() {
      this.showAddModal = false;
      this.resetForm();
    },
    
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    getTypeStyles(type) {
      const styles = {
        'event': 'bg-blue-100 text-blue-800',
        'task': 'bg-green-100 text-green-800',
        'reminder': 'bg-yellow-100 text-yellow-800',
        'study-session': 'bg-purple-100 text-purple-800'
      };
      return styles[type] || 'bg-gray-100 text-gray-800';
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
